/** i18n registry — maps language codes to translation dictionaries. */
import { el, type TranslationKey } from './el.js';
import { en } from './en.js';

export type { TranslationKey };
export type LangCode = string;

const translations: Record<string, Record<TranslationKey, string>> = { el, en };

/**
 * Look up a translation for the given key and language.
 * Falls back to Greek ('el') when the key is missing in the target language.
 */
export function getTranslation(lang: LangCode, key: TranslationKey): string {
  return translations[lang]?.[key] ?? translations.el[key] ?? key;
}

// ---------------------------------------------------------------------------
// Greek→English display translations for facet values (categories, countries)
// The *value* sent to the API stays Greek; only the *label* is translated.
// ---------------------------------------------------------------------------
const FACET_LABEL_EL_TO_EN: Record<string, string> = {
  // Categories
  'Κόκκινο': 'Red',
  'Λευκό': 'White',
  'Ροζέ': 'Rosé',
  'Ροζέ/Αφρώδη/Επιδόρπια': 'Rosé / Sparkling / Dessert',
  'Πορτοκαλί': 'Orange',
  'Αφρώδη': 'Sparkling',
  'Αφρώδες': 'Sparkling',
  'Επιδόρπια': 'Dessert',
  'Επιδόρπια/Γλυκά': 'Dessert / Sweet',
  'Γλυκά': 'Sweet',
  'Φυσικά κρασιά': 'Natural Wines',
  'Orange Wines': 'Orange Wines',
  'Apostagmata-Spirits': 'Spirits',
  'Αξεσουάρ': 'Accessories',
  'Deli': 'Deli',
  'Wine Experiences': 'Wine Experiences',
  'Δώρα': 'Gifts',
  'Εξάδες': 'Wine Sets',
  'Συσκευασίες Δώρου': 'Gift Boxes',

  // Countries
  'Ελλάδα': 'Greece',
  'Γαλλία': 'France',
  'Ιταλία': 'Italy',
  'Ισπανία': 'Spain',
  'Γερμανία': 'Germany',
  'Αργεντινή': 'Argentina',
  'Αυστραλία': 'Australia',
  'Νέα Ζηλανδία': 'New Zealand',
  'Χιλή': 'Chile',
  'Νότια Αφρική': 'South Africa',
  'Αμερική': 'America',
  'ΗΠΑ': 'USA',
  'Πορτογαλία': 'Portugal',
  'Αυστρία': 'Austria',
  'Σκωτία': 'Scotland',
  'Ιαπωνία': 'Japan',
  'Ιρλανδία': 'Ireland',
  'Μεξικό': 'Mexico',
  'Ολλανδία': 'Netherlands',
  'Κολομβία': 'Colombia',
  'Ηνωμένο Βασίλειο': 'United Kingdom',
  'Σλοβενία': 'Slovenia',

  // Regions
  'Σαντορίνη': 'Santorini',
  'Νεμέα': 'Nemea',
  'Νάουσα': 'Naoussa',
  'Θεσσαλονίκη': 'Thessaloniki',
  'Προβηγκία': 'Provence',
  'Κεφαλονιά': 'Kefalonia',
  'Κορινθία': 'Corinthia',
  'Δράμα': 'Drama',
  'Αττική': 'Attica',
  'Εύβοια': 'Evia',
  'Βουργουνδία': 'Burgundy',
  'Αιγιαλεία': 'Aigialeia',
  'Τοσκάνη': 'Tuscany',
  'Βοιωτία': 'Boeotia',
  'Αμύνταιο': 'Amynteo',
  'Πέλλα': 'Pella',
  'Καμπανία': 'Campania',
  'Μαντίνεια': 'Mantinia',
  'Μαντινεία': 'Mantinia',
  'Γουμένισσα': 'Goumenissa',
  'Ολυμπία': 'Olympia',
  'Χανιά, Κρήτη': 'Chania, Crete',
  'Ηράκλειο, Κρήτη': 'Heraklion, Crete',
  'Άβδηρα, Ξάνθη': 'Avdira, Xanthi',
};

/**
 * Translate a Greek facet label to English for display.
 * Returns the original label if no translation is found or lang is not 'en'.
 */
export function translateFacetLabel(lang: LangCode, label: string): string {
  if (lang !== 'en') return label;
  return FACET_LABEL_EL_TO_EN[label] ?? label;
}


/**
 * Interpolate `{param}` placeholders inside a translated string.
 *
 * Example:
 *   interpolate('{count} results in {time}ms', { count: 42, time: 12 })
 *   → '42 results in 12ms'
 */
export function interpolate(
  template: string,
  params?: Record<string, string | number>,
): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    params[key] !== undefined ? String(params[key]) : `{${key}}`,
  );
}
